export const SENSITIVE_KEYS = new Set([
  'accessToken',
  'accessTokens',
  'anonymousApiKey',
  'apiKey',
  'apiKeyId',
  'apiKeys',
  'client_secret',
  'clientSecret',
  'hostedInvoiceURL',
  'invoicePDF',
  'jwk',
  'password',
  'paymentMethod',
  'secretApiKey',
  'secretValue',
  'stripePaymentMethodID',
  'token',
]);

export const SENSITIVE_URL_PARAMS = new Set(['jwt', 'apiKey', 'token', 'code']);

export const REDACTED = '[REDACTED]';

type RedactResult = {value: unknown; changed: boolean};

function redactValue(value: unknown): RedactResult {
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map(item => {
      const result = redactValue(item);
      changed ||= result.changed;
      return result.value;
    });
    return {value: changed ? next : value, changed};
  }

  if (value !== null && typeof value === 'object') {
    let changed = false;
    const obj = value as Record<string, unknown>;
    const next: Record<string, unknown> = {};
    for (const [key, fieldValue] of Object.entries(obj)) {
      if (SENSITIVE_KEYS.has(key)) {
        next[key] = REDACTED;
        changed = true;
      } else {
        const result = redactValue(fieldValue);
        next[key] = result.value;
        changed ||= result.changed;
      }
    }
    return {value: changed ? next : value, changed};
  }

  return {value, changed: false};
}

/**
 * Parses a JSON string, redacts any keys that match SENSITIVE_KEYS at any
 * nesting depth, and returns the re-serialized string. Returns the original
 * if nothing was redacted. Returns REDACTED if the JSON is malformed.
 */
export function redactJsonField(raw: string | undefined): string | undefined {
  if (!raw) {
    return raw;
  }
  try {
    const parsed = JSON.parse(raw);
    const {value, changed} = redactValue(parsed);
    return changed ? JSON.stringify(value) : raw;
  } catch {
    return REDACTED;
  }
}

/**
 * Like redactJsonField but targets `.variables` inside a JSON payload
 * (the shape sent by GraphQL clients: `{query, variables}`).
 * Non-JSON payloads (raw query strings) are returned unchanged.
 */
export function redactPayloadField(
  raw: string | undefined
): string | undefined {
  if (!raw) {
    return raw;
  }
  try {
    const parsed = JSON.parse(raw);
    if (!parsed.variables) {
      return raw;
    }
    const {value, changed} = redactValue(parsed.variables);
    if (!changed) {
      return raw;
    }
    parsed.variables = value;
    return JSON.stringify(parsed);
  } catch {
    return raw;
  }
}
